import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../../features/admin/service";
import type { Campaign, CampaignAnalytics } from "../../features/boost/types";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

const BASE = "/api/admin/boost";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt(p: number) { return `₹${(p / 100).toLocaleString("en-IN")}`; }

const STATUS_OPTIONS = ["", "ACTIVE", "PAUSED", "DAILY_CAP_REACHED", "BUDGET_EXHAUSTED", "EXPIRED", "CANCELLED"] as const;

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "bg-emerald-500/10 text-emerald-400",
  PAUSED: "bg-amber-500/10 text-amber-400",
  // Today's spend cap hit — resumes automatically tomorrow (not an error state).
  DAILY_CAP_REACHED: "bg-sky-500/10 text-sky-400",
  BUDGET_EXHAUSTED: "bg-orange-500/10 text-orange-400",
  EXPIRED: "bg-gray-500/10 text-gray-400",
  CANCELLED: "bg-red-500/10 text-red-400",
};

/** Auction knobs. These decide what advertisers actually pay — RESERVE_PRICE is
 *  the main revenue lever (the cost of a view nobody competed for). */
const PRICING_KNOBS: { key: string; label: string; hint: string; unit: string }[] = [
  { key: "BOOST_RESERVE_PRICE_PAISE", label: "Reserve price", unit: "paise / view",
    hint: "What an UNCONTESTED view costs. The floor of every auction — the main revenue lever. Must be > 0: a boosted deal still outranks all organic results, so it is delivering value even with no rival." },
  { key: "BOOST_MIN_BID_PAISE", label: "Minimum bid", unit: "paise / view",
    hint: "Lowest bid a seller may set. Bids below the reserve can never win." },
  { key: "BOOST_MAX_BID_PAISE", label: "Maximum bid", unit: "paise / view",
    hint: "Bid ceiling — abuse guard." },
  { key: "BOOST_SPONSORED_SLOTS", label: "Sponsored slots", unit: "per page",
    hint: "How many ads can win a page. Losers get no lift and are never charged." },
  { key: "BOOST_RELEVANCE_FLOOR_BP", label: "Relevance floor", unit: "basis points (1500 = 0.15)",
    hint: "Minimum search relevance to compete. Stops money dragging an irrelevant deal onto the page." },
  { key: "BOOST_QUALITY_WEIGHTING", label: "Quality weighting", unit: "1 = on, 0 = off",
    hint: "On: ad_rank = bid x relevance, so a rich but irrelevant deal cannot buy the top slot. Off: pure pay-to-win." },
];

export default function AdminBoostPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [analyticsOpen, setAnalyticsOpen] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<Record<string, CampaignAnalytics>>({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [capsEditing, setCapsEditing] = useState<string | null>(null);
  const [capsForm, setCapsForm] = useState<{ max_impressions: string; max_clicks: string; max_search_appearances: string }>({ max_impressions: "", max_clicks: "", max_search_appearances: "" });
  const [capsSaving, setCapsSaving] = useState(false);
  const [pricing, setPricing] = useState<Record<string, { value: number; default: number }>>({});
  const [pricingDraft, setPricingDraft] = useState<Record<string, string>>({});
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingMsg, setPricingMsg] = useState("");

  const loadPricing = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`${BASE}/pricing`, { headers: adminHeaders() });
      setPricing(data.pricing || {});
      const draft: Record<string, string> = {};
      Object.entries(data.pricing || {}).forEach(([k, v]) => {
        draft[k] = String((v as { value: number }).value);
      });
      setPricingDraft(draft);
    } catch {
      /* pricing panel is non-critical — never block the campaign list */
    }
  }, []);

  async function savePricing() {
    setPricingSaving(true);
    setPricingMsg("");
    try {
      const updates: Record<string, number> = {};
      Object.entries(pricingDraft).forEach(([k, v]) => {
        const n = Number(v);
        if (!Number.isNaN(n) && n !== pricing[k]?.value) updates[k] = n;
      });
      if (Object.keys(updates).length === 0) {
        setPricingMsg("No changes");
      } else {
        const { data } = await apiClient.put(`${BASE}/pricing`, updates, { headers: adminHeaders() });
        setPricing(data.pricing || {});
        setPricingMsg("Saved — applies to the next auction");
      }
    } catch (e) {
      setPricingMsg(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setPricingSaving(false);
    }
  }

  useEffect(() => { void loadPricing(); }, [loadPricing]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (sellerFilter.trim()) params.seller_id = sellerFilter.trim();
      const { data } = await apiClient.get<Campaign[]>(`${BASE}/campaigns`, {
        params,
        headers: adminHeaders(),
      });
      setCampaigns(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [statusFilter]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([load(), loadPricing()]).then(() => {});
  }, [load, loadPricing]);

  async function toggleAnalytics(id: string) {
    if (analyticsOpen === id) { setAnalyticsOpen(null); return; }
    setAnalyticsOpen(id);
    if (analyticsData[id]) return;
    setAnalyticsLoading(true);
    try {
      const { data } = await apiClient.get<CampaignAnalytics>(
        `${BASE}/campaigns/${id}/analytics`,
        { headers: adminHeaders() },
      );
      setAnalyticsData(prev => ({ ...prev, [id]: data }));
    } catch { /* silently skip */ }
    finally { setAnalyticsLoading(false); }
  }

  function openCapsEditor(c: Campaign) {
    setCapsEditing(c.id);
    setCapsForm({
      max_impressions: c.max_impressions != null ? String(c.max_impressions) : "",
      max_clicks: c.max_clicks != null ? String(c.max_clicks) : "",
      max_search_appearances: c.max_search_appearances != null ? String(c.max_search_appearances) : "",
    });
  }

  async function saveCaps(id: string) {
    setCapsSaving(true);
    try {
      const { data } = await apiClient.put<Campaign>(
        `${BASE}/campaigns/${id}/caps`,
        {
          max_impressions: capsForm.max_impressions ? parseInt(capsForm.max_impressions, 10) : null,
          max_clicks: capsForm.max_clicks ? parseInt(capsForm.max_clicks, 10) : null,
          max_search_appearances: capsForm.max_search_appearances ? parseInt(capsForm.max_search_appearances, 10) : null,
        },
        { headers: adminHeaders() },
      );
      setCampaigns(prev => prev.map(c => c.id === id ? data : c));
      setCapsEditing(null);
    } catch {
      setError("Failed to save caps");
    } finally {
      setCapsSaving(false);
    }
  }

  async function handleAction(id: string, action: "pause" | "resume" | "cancel") {
    setActing(id);
    try {
      const { data } = await apiClient.patch<Campaign>(
        `${BASE}/campaigns/${id}`,
        { action },
        { headers: adminHeaders() },
      );
      setCampaigns(prev => prev.map(c => c.id === id ? data : c));
    } catch {
      setError("Action failed");
    } finally {
      setActing(null);
    }
  }

  const totalImp = campaigns.reduce((s, c) => s + (c.impression_count ?? 0), 0);
  const totalClk = campaigns.reduce((s, c) => s + (c.click_count ?? 0), 0);
  const totalSpent = campaigns.reduce((s, c) => s + c.spent_paise, 0);
  const activeCount = campaigns.filter(c => c.status === "ACTIVE").length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-white">Boost Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and monitor all seller boost campaigns</p>
        </div>
        <RefreshButton onRefresh={handleRefreshAll} className="bg-gray-800 hover:bg-gray-700" />
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Active", value: activeCount },
          { label: "Impressions", value: totalImp.toLocaleString("en-IN") },
          { label: "Clicks", value: totalClk.toLocaleString("en-IN") },
          { label: "Total Spent", value: fmt(totalSpent) },
        ].map(s => (
          <div key={s.label} className="bg-gray-900 rounded-xl border border-gray-800 p-4 text-center">
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Auction pricing — live, no deploy needed */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-sm font-semibold text-white">Auction Pricing</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Sellers are charged per delivered view, at a price set by competition
              (second-price auction). Changes apply to the next auction — no deploy.
            </p>
          </div>
          <button
            onClick={() => void savePricing()}
            disabled={pricingSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            {pricingSaving ? "Saving…" : "Save"}
          </button>
        </div>
        {pricingMsg && <p className="text-xs text-blue-600 mb-2">{pricingMsg}</p>}
        <div className="grid grid-cols-2 gap-4 mt-3">
          {PRICING_KNOBS.map(k => (
            <div key={k.key}>
              <label className="text-xs font-medium text-gray-300 block">{k.label}</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  value={pricingDraft[k.key] ?? ""}
                  onChange={e => setPricingDraft(p => ({ ...p, [k.key]: e.target.value }))}
                  className="w-32 border border-gray-700 bg-gray-800 text-white rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <span className="text-xs text-gray-400">{k.unit}</span>
                {pricing[k.key] && pricing[k.key].value !== pricing[k.key].default && (
                  <span className="text-[10px] text-amber-600">
                    (default {pricing[k.key].default})
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-1 leading-snug">{k.hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-700 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s || "All statuses"}</option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-auto">
          <label className="text-xs font-medium text-gray-500 block mb-1">Seller ID</label>
          <input
            value={sellerFilter}
            onChange={e => setSellerFilter(e.target.value)}
            onKeyDown={e => e.key === "Enter" && void load()}
            placeholder="Filter by seller ID…"
            className="border border-gray-700 bg-gray-800 text-white placeholder-gray-500 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-full sm:w-56"
          />
        </div>
        <button onClick={() => void load()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
          Search
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* Campaigns table */}
      {loading ? (
        <div className="py-16 text-center text-gray-400"><Spinner /></div>
      ) : campaigns.length === 0 ? (
        <div className="py-16 text-center text-gray-400">No campaigns found</div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const isActing = acting === c.id;
            const ctr = c.impression_count ? ((c.click_count ?? 0) / c.impression_count * 100).toFixed(1) : "—";
            const analytics = analyticsData[c.id];

            return (
              <div key={c.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-white text-sm truncate max-w-xs">
                          {c.deal_title ?? c.deal_id}
                        </p>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLORS[c.status] ?? "bg-gray-500/10 text-gray-400"}`}>
                          {c.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5 font-mono">{c.seller_id} · {c.deal_id}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                        <span>{c.surfaces.join(" + ")}</span>
                        {c.target_countries.length > 0 && <span>{c.target_countries.join(", ")}</span>}
                        {c.deal_categories.length > 0 && <span>{c.deal_categories.slice(0, 4).join(", ")}{c.deal_categories.length > 4 ? "…" : ""}</span>}
                      </div>
                    </div>

                    <div className="grid grid-cols-5 gap-3 sm:gap-4 text-center sm:flex-shrink-0">
                      {[
                        { label: "Impr.", value: (c.impression_count ?? 0).toLocaleString("en-IN") },
                        { label: "Clicks", value: (c.click_count ?? 0).toLocaleString("en-IN") },
                        { label: "CTR", value: `${ctr}%` },
                        { label: "Spent", value: fmt(c.spent_paise) },
                        { label: "Budget", value: fmt(c.total_budget_paise) },
                      ].map(m => (
                        <div key={m.label}>
                          <p className="text-[10px] text-gray-400">{m.label}</p>
                          <p className="text-xs font-semibold text-white">{m.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    {c.status === "ACTIVE" && (
                      <button onClick={() => void handleAction(c.id, "pause")} disabled={isActing}
                        className="px-3 py-1 text-xs font-semibold border border-amber-500/30 text-amber-400 rounded-lg hover:bg-amber-500/10 disabled:opacity-60">
                        {isActing ? "…" : "Pause"}
                      </button>
                    )}
                    {c.status === "PAUSED" && (
                      <button onClick={() => void handleAction(c.id, "resume")} disabled={isActing}
                        className="px-3 py-1 text-xs font-semibold border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/10 disabled:opacity-60">
                        {isActing ? "…" : "Resume"}
                      </button>
                    )}
                    {["ACTIVE", "PAUSED", "BUDGET_EXHAUSTED"].includes(c.status) && (
                      <button onClick={() => void handleAction(c.id, "cancel")} disabled={isActing}
                        className="px-3 py-1 text-xs font-semibold border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 disabled:opacity-60">
                        {isActing ? "…" : "Cancel"}
                      </button>
                    )}
                    <button onClick={() => void toggleAnalytics(c.id)}
                      className={`px-3 py-1 text-xs font-semibold border rounded-lg transition-colors ${analyticsOpen === c.id ? "bg-blue-600 text-white border-blue-600" : "border-gray-700 text-gray-400 hover:border-blue-400 hover:text-white"}`}>
                      Analytics
                    </button>
                    <button onClick={() => capsEditing === c.id ? setCapsEditing(null) : openCapsEditor(c)}
                      className={`px-3 py-1 text-xs font-semibold border rounded-lg transition-colors ${capsEditing === c.id ? "bg-violet-600 text-white border-violet-600" : "border-gray-700 text-gray-400 hover:border-violet-400 hover:text-white"}`}>
                      Edit Caps
                    </button>
                    <span className="ml-auto text-[10px] text-gray-400 self-center">
                      {new Date(c.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      {" → "}
                      {new Date(c.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                </div>

                {capsEditing === c.id && (
                  <div className="border-t border-violet-500/20 px-4 py-3 bg-violet-500/10">
                    <p className="text-xs font-semibold text-violet-300 mb-3">Event Caps — leave blank to remove cap</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: "max_impressions" as const, label: "Max Impressions" },
                        { key: "max_clicks" as const, label: "Max Clicks" },
                        { key: "max_search_appearances" as const, label: "Max Searches" },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label className="text-[10px] text-gray-500 block mb-1">{label}</label>
                          <input
                            type="number"
                            min={1}
                            value={capsForm[key]}
                            onChange={e => setCapsForm(prev => ({ ...prev, [key]: e.target.value }))}
                            placeholder="No cap"
                            className="w-full border border-gray-700 bg-gray-800 text-white rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-violet-500 outline-none"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => void saveCaps(c.id)} disabled={capsSaving}
                        className="px-4 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-60 transition-colors">
                        {capsSaving ? "Saving…" : "Save Caps"}
                      </button>
                      <button onClick={() => setCapsEditing(null)}
                        className="px-4 py-1.5 border border-gray-700 text-gray-400 text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {analyticsOpen === c.id && (
                  <div className="border-t border-gray-800 px-4 py-3 bg-gray-800/50">
                    {analyticsLoading && !analytics ? (
                      <p className="text-xs text-gray-400 text-center py-2">Loading analytics…</p>
                    ) : analytics ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                          {[
                            { label: "Impressions", value: analytics.total_impressions.toLocaleString("en-IN") },
                            { label: "Clicks", value: analytics.total_clicks.toLocaleString("en-IN") },
                            { label: "CTR", value: `${(analytics.ctr * 100).toFixed(1)}%` },
                            { label: "Searches", value: analytics.total_search_appearances.toLocaleString("en-IN") },
                          ].map(m => (
                            <div key={m.label} className="bg-gray-900 rounded-lg p-2 border border-gray-800">
                              <p className="text-sm font-bold text-white">{m.value}</p>
                              <p className="text-[10px] text-gray-400">{m.label}</p>
                            </div>
                          ))}
                        </div>
                        {analytics.days.length > 0 && (
                          <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-400 border-b border-gray-800">
                                <th className="pb-1 text-left font-medium">Date</th>
                                <th className="pb-1 text-right font-medium">Impr.</th>
                                <th className="pb-1 text-right font-medium">Clicks</th>
                                <th className="pb-1 text-right font-medium">CTR</th>
                                <th className="pb-1 text-right font-medium">Searches</th>
                                <th className="pb-1 text-right font-medium">Spend</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.days.map(d => (
                                <tr key={d.date} className="border-b border-gray-800 last:border-0">
                                  <td className="py-1 text-gray-400">{d.date}</td>
                                  <td className="py-1 text-right">{d.impressions.toLocaleString("en-IN")}</td>
                                  <td className="py-1 text-right">{d.clicks.toLocaleString("en-IN")}</td>
                                  <td className="py-1 text-right">{d.impressions ? `${(d.clicks / d.impressions * 100).toFixed(1)}%` : "—"}</td>
                                  <td className="py-1 text-right">{d.search_appearances.toLocaleString("en-IN")}</td>
                                  <td className="py-1 text-right">{fmt(d.spend_paise)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        )}
                        {analytics.days.length === 0 && (
                          <p className="text-xs text-gray-400 text-center py-2">No events recorded yet.</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-2">Failed to load analytics.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
